// Color contrast utilities for lamp wireframes

export const MIN_CONTRAST_RATIO = 4.5;

function luminance(r, g, b) {
  let a = [r, g, b].map(function (v) {
    return v <= 0.03928
      ? v / 12.92
      : Math.pow((v + 0.055) / 1.055, 2.4);
  });
  return 0.2126 * a[0] + 0.7152 * a[1] + 0.0722 * a[2];
}

function contrast(rgb1, rgb2) {
  const lum1 = luminance(...rgb1);
  const lum2 = luminance(...rgb2);
  const brightest = Math.max(lum1, lum2);
  const darkest = Math.min(lum1, lum2);
  return (brightest + 0.05) / (darkest + 0.05);
}

// Convert hex color to RGB array [r, g, b] with values in [0,1]
function hexToRGB(hex) {
  const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
  return result ? [
    parseInt(result[1], 16) / 255,
    parseInt(result[2], 16) / 255,
    parseInt(result[3], 16) / 255
  ] : [0, 0, 0];
}

// Convert RGB array [r, g, b] with values in [0,1] to hex
function rgbToHex(r, g, b) {
  const toHex = (c) => {
    const hex = Math.round(c * 255).toString(16);
    return hex.length === 1 ? '0' + hex : hex;
  };
  return `#${toHex(r)}${toHex(g)}${toHex(b)}`;
}

// lampColor: { r, g, b } with values in [0,1]
export function pickWireframeGray(lampColor, minContrast = MIN_CONTRAST_RATIO) {
  const lampRGB = [lampColor.r, lampColor.g, lampColor.b];
  let bestGray = null;
  let lowestContrast = Infinity;
  for (let gray = 0; gray <= 255; gray++) {
    const g = gray / 255;
    const grayRGB = [g, g, g];
    const c = contrast(lampRGB, grayRGB);
    if (c >= minContrast && c < lowestContrast) {
      lowestContrast = c;
      bestGray = g;
    }
  }
  // Fallback: if no gray meets the minimum, pick the one with the highest contrast
  if (bestGray === null) {
    let bestContrast = 0;
    let fallbackGray = 0;
    for (let gray = 0; gray <= 255; gray++) {
      const g = gray / 255;
      const grayRGB = [g, g, g];
      const c = contrast(lampRGB, grayRGB);
      if (c > bestContrast) {
        bestContrast = c;
        fallbackGray = g;
      }
    }
    bestGray = fallbackGray;
    lowestContrast = bestContrast;
  }
  const wireframeRGB = [bestGray, bestGray, bestGray];
  const actualContrast = contrast(lampRGB, wireframeRGB);
  //console.log('[pickWireframeGray] Lamp color:', lampColor, 'Chosen wireframe gray:', bestGray, 'Actual contrast:', actualContrast);
  return { r: bestGray, g: bestGray, b: bestGray };
}

// Generate a random color that meets contrast requirements with a given background
export function generateCompliantColor(backgroundColor, minContrast = MIN_CONTRAST_RATIO) {
  const bgRGB = hexToRGB(backgroundColor);
  
  // Try multiple random colors to find one that meets contrast requirements
  for (let attempt = 0; attempt < 100; attempt++) {
    const r = Math.random();
    const g = Math.random();
    const b = Math.random();
    
    const colorRGB = [r, g, b];
    const contrastRatio = contrast(colorRGB, bgRGB);
    
    if (contrastRatio >= minContrast) {
      const hexColor = rgbToHex(r, g, b);
      return hexColor;
    }
  }
  
  // Fallback: generate a color with maximum contrast
  const bgLuminance = luminance(...bgRGB);
  const targetLuminance = bgLuminance > 0.5 ? 0.1 : 0.9; // Opposite luminance
  
  // Simple approach: create a color with the target luminance
  const fallbackColor = rgbToHex(targetLuminance, targetLuminance, targetLuminance);
  return fallbackColor;
}

// Generate a compliant color pair (lamp color and background color)
export function generateCompliantColorPair(minContrast = MIN_CONTRAST_RATIO) {
  // Start with a random background color
  const backgroundR = Math.random();
  const backgroundG = Math.random();
  const backgroundB = Math.random();
  const backgroundColor = rgbToHex(backgroundR, backgroundG, backgroundB);
  
  // Generate a lamp color that contrasts well with the background
  const lampColor = generateCompliantColor(backgroundColor, minContrast);
  
  // Also generate an emissive color that contrasts well with the lamp color
  const emissiveColor = generateCompliantColor(lampColor, minContrast * 0.8); // Slightly lower contrast for emissive
  
  return {
    backgroundColor,
    lampColor,
    emissiveColor
  };
}

// Generate a random color with aesthetic considerations (avoiding pure black/white)
export function generateAestheticColor() {
  // Generate colors with some saturation and avoid pure grays
  const hue = Math.random() * 360;
  const saturation = 0.3 + Math.random() * 0.7; // 30-100% saturation
  const lightness = 0.2 + Math.random() * 0.6; // 20-80% lightness
  
  // Convert HSL to RGB
  const h = hue / 60;
  const c = (1 - Math.abs(2 * lightness - 1)) * saturation;
  const x = c * (1 - Math.abs(h % 2 - 1));
  const m = lightness - c / 2;
  
  let r, g, b;
  if (h < 1) {
    [r, g, b] = [c, x, 0];
  } else if (h < 2) {
    [r, g, b] = [x, c, 0];
  } else if (h < 3) {
    [r, g, b] = [0, c, x];
  } else if (h < 4) {
    [r, g, b] = [0, x, c];
  } else if (h < 5) {
    [r, g, b] = [x, 0, c];
  } else {
    [r, g, b] = [c, 0, x];
  }
  
  return rgbToHex(r + m, g + m, b + m);
}

// Generate a compliant color pair with aesthetic considerations
export function generateAestheticCompliantColorPair(minContrast = MIN_CONTRAST_RATIO) {
  // Start with an aesthetic background color
  const backgroundColor = generateAestheticColor();
  
  // Generate an aesthetic lamp color that contrasts well
  const lampColor = generateCompliantColor(backgroundColor, minContrast);
  
  // Generate an aesthetic emissive color
  const emissiveColor = generateAestheticColor();
  
  // Ensure emissive color contrasts with lamp color
  const finalEmissiveColor = generateCompliantColor(lampColor, minContrast * 0.7);
  
  return {
    backgroundColor,
    lampColor,
    emissiveColor: finalEmissiveColor
  };
} 